import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { isNullOrUndefined, isUndefined } from '../../models/utils';
import { DrObject, DrObjectType, SupportedRegExType } from '../../models/dr-object';
import { Endpoint } from '../../models/enums';
import { TagViewModel } from '../../models/tag';
import { RestService } from '../../services/rest/rest.service';
import { SYS_GENERATED_DOMAIN_LIST } from '../../models/constants';

@Component({
    standalone: false,
    selector: 'res-name-description',
    templateUrl: './name-description.component.html',
    styleUrls: ['./name-description.component.scss']
})

export class NameDescriptionComponent implements OnInit {
    // name member variables
    @Input() name: string;
    @Input() maxNameLength: number;
    @Input() isNameDisabled = false;
    @Input() nameLabel = 'Name:';
    @Input() onlyOnSave: boolean;
    @Input() validateName: boolean;
    @Input() disableErrorPopover = false;
    @Input() popoverStickyClass: string;
    @Input() tooltipName: string;
    @Input() validateNameRequest: {
        endpoint: Endpoint,
        request: (name: string) => void
    };

    // if you need a name validated, then you need a drObject.
    drObject: DrObject;
    private originalName: string;
    private validateNameNeeded: boolean;

    // description member variables
    @Input() description: string;
    private originalDescription: string;
    @Input() maxDescriptionLength: number;
    @Input() isDescriptionDisabled = false;
    @Input() isDescriptionHidden = false;

    // all other variables
    @Input() automationId: string;
    @Input() customClassRow: string;
    @Input() middleRowTemplate: TemplateRef<any>;
    @Input() bottomRowTemplate: TemplateRef<any>;
    @Input() customLabelWidth: string;
    @Input() changeInputStyleWhenNotValid = false;

    formValid = false;
    isBusy = false;
    isBusyMessage: string;
    validateNameError: string;
    nameCaseSensitive = true;
    showErrorPopover = false;
    errPopoverSingleLine: boolean;
    isNameChanged = false;

    constructor(private restService: RestService) { }

    ngOnInit(): void {
        this.initialize();
    }

    initialize(): void {
        this.validateNameNeeded = false;

        if (this.name) {
            this.originalName = this.name.toLowerCase();
        }

        if (this.description) {
            this.originalDescription = this.description;
        }

        if (isUndefined(this.originalDescription)) {
            this.originalDescription = '';
        }

        if (isUndefined(this.description)) {
            this.description = '';
        }

        if (isUndefined(this.tooltipName)) {
            this.tooltipName = '';
        }

        this.maxNameLength = this.drObject.maxNameLength;
        this.maxDescriptionLength = this.drObject.maxDescriptionLength;
        const isOrgOrSystemRole = this.drObject.objectType === DrObjectType.ROLE || this.drObject.objectType === DrObjectType.SYSTEM_ROLE;
        this.errPopoverSingleLine = this.drObject && this.drObject.objectType && isOrgOrSystemRole ? true : false;
        this.isNameChanged = false;
    }

    private isReadyForValidation(text: string, original: string): boolean {
        let isReady = false;
        if (!isUndefined(text) && (text !== null)) {
            if (original !== text) {
                isReady = true;
            }
        }
        return isReady;
    }

    isNameDirty(): boolean {
        if (!isUndefined(this.name) && (this.name !== null)) {
            return this.isReadyForValidation(this.name.toLowerCase(), this.originalName);
        }
    }

    getOriginalName(): string {
        return this.originalName;
    }

    setOriginalName(name: string): void {
        this.originalName = name.toLowerCase();
    }

    isDescriptionDirty(): boolean {
        return this.isReadyForValidation(this.description, this.originalDescription);
    }

    getOriginalDescription(): string {
        return this.originalDescription;
    }

    formatName(): string {
        this.name = this.name.trim();
        return this.name.toLowerCase();
    }

    checkForDomainListReservedName(): void {
        const isDomainListEntity = this.drObject?.objectType === DrObjectType.DOMAIN_LIST;
        if (isDomainListEntity) {
            if (this.name?.toUpperCase() === SYS_GENERATED_DOMAIN_LIST) {
                this.formValid = false;
                this.changeInputStyleWhenNotValid = true;
                this.validateNameError = `${SYS_GENERATED_DOMAIN_LIST} is a system-reserved template name and may not be used.`;
                this.displayError();
            } else {
                this.changeInputStyleWhenNotValid = false;
            }
        }
    }

    onNameChanged(): void {
        this.formValid = false;
        this.validateNameError = null;
        this.validateNameNeeded = false;
        this.name.replace(/^\s+/, '');  // remove leading whitespaces
        this.nameValidateCharacters();
        if (this.validateName) {
            const isReady = this.isReadyForValidation(this.name, this.originalName);
            if (isReady) {
                this.validateNameNeeded = true;
            }
        }
        this.isFormValid();
        this.isNameChanged = (!isNullOrUndefined(this.name) && this.name.length > 0 && this.name !== this.originalName) ? true : false;
    }

    onDescriptionChanged(): void {
        this.description.replace(/^\s+/, '');  // remove leading whitespaces
        const isReady = this.isReadyForValidation(this.description, this.originalDescription);
        if (isReady) {
            this.isFormValid();
        }
    }

    onDescriptionFocusLost(): void {
        if (!isUndefined(this.description) && (this.description !== null)) {
            this.description = this.description.trim();
        }
    }

    onFocus(): void {
        this.validateNameError = null;
        const isReady = this.isReadyForValidation(this.name, this.originalName);
        if (isReady) {
            if (this.nameValidateCharacters() === true) {
                this.nameValidateCharacters();
            }

            this.isFormValid();

            // what them to fix the bad characters first, if any.
            if (this.formValid === true) {
                if (this.validateName) {
                    this.validateNameNeeded = true;
                }

                if (!this.onlyOnSave) {
                    this.validate();
                }
            }
        }
    }

    onNameFocusLost(event?: any): void {
        let isCancelOrClosedOrErrorAlreadyOpen = false;
        if (!isNullOrUndefined(event)) { // add to support testing
            if (event?.relatedTarget?.classList?.value) {
                const isAddRemoveMembersButton = event?.relatedTarget?.lastChild?.nodeValue === 'Add / Remove Members ... '
                isCancelOrClosedOrErrorAlreadyOpen = ((event.relatedTarget.classList.value === 'secondary-button' && !isAddRemoveMembersButton)
                    || (event.relatedTarget.classList.value === 'close-button')
                    || this.showErrorPopover);
            }
        }

        if (!isCancelOrClosedOrErrorAlreadyOpen) {
            const isReady = this.isReadyForValidation(this.name, this.originalName);
            if (isReady) {
                this.formatName();
                this.validateNameNeeded = false;
                if (this.nameValidateCharacters() === true) {
                    if (this.validateName) {
                        this.validateNameNeeded = true;
                    }
                    if (!this.onlyOnSave) {
                        this.validate();
                    }
                }
            }
            this.isFormValid();
        }
    }

    validate(): Promise<boolean> {
        this.isBusyMessage = `Validating Name ...`;
        let trimmedName = 'undefined';
        if (this.name) {
            trimmedName = this.formatName(); // trimmed and lowercased
        }
        if (this.validateNameNeeded && trimmedName && (trimmedName !== this.originalName) && this.validateNameError == null && this.validateNameRequest && this.validateNameRequest.endpoint) {
            this.showErrorPopover = false;
            const requestMessage = this.validateNameRequest.request(trimmedName);
            if (!isNullOrUndefined(requestMessage)) {
                this.isBusy = true;
                return new Promise<boolean>((resolve) => {
                    this.restService.post(this.validateNameRequest.endpoint, requestMessage).then(response => {
                        this.validateNameNeeded = false;
                        this.isBusy = false;
                        this.formValid = true;
                        if (response && !response.valid) {
                            this.formValid = false;
                            this.validateNameError = response.reason;
                            this.showErrorPopover = true;
                        }
                        resolve(this.formValid);
                        this.isBusyMessage = ``;
                    }, error => {
                        this.isBusy = false;
                    });
                });
            } else {
                return Promise.resolve(false);
            }
        } else {
            return Promise.resolve(this.formValid);
        }
    }

    set(drObject: DrObject, name: string, description: string, validateName?: boolean): void {
        if (drObject) {
            this.drObject = drObject;
        }

        if (name) {
            this.name = name;
        }

        if (description) {
            this.description = description;
        }

        if (validateName) {
            this.validateName = validateName;
        }
    }

    createDrObject(type: DrObjectType, regex?: SupportedRegExType, validateName?: boolean): void {
        this.drObject = new DrObject(type);
        if (regex) {
            this.drObject.regex = regex;
        }

        if (validateName) {
            this.validateName = validateName;
        }
    }

    private nameValidateCharacters(): boolean {
        let isValid = true;
        this.validateNameError = null;

        // do we have good characters
        this.showErrorPopover = false;
        this.validateNameError = this.validateCharacters();

        if (this.validateNameError) {
            this.displayError();
            isValid = false;
        }

        // if we have good characters, do we have and reserved word conflicts
        if (this.isFolderName() && (this.validateNameError == null)) {
            this.validateNameError = this.validateFolderName();
            if (this.validateNameError) {
                this.displayError();
                isValid = false;
            }
        }
        return isValid;
    }

    private validateCharacters(): string {
        this.isFormValid();
        this.validateNameError = null;
        if (this.drObject) {
            return this.drObject.uiValidateName(this.name);
        }
    }

    private isFolderName(): boolean {
        if (this.drObject && this.drObject.objectType && this.validateNameRequest && this.validateNameRequest.request) {
            return this.drObject.objectType === DrObjectType.TAG || this.drObject.objectType === DrObjectType.DYNAMIC_FOLDER;
        }
        return false;
    }

    private validateFolderName(): string {
        this.validateNameError = null;
        return TagViewModel.validateReservedWords(this.name);
    }

    private displayError() {
        if (this.validateNameError) {
            this.validateNameNeeded = false;
            if (this.disableErrorPopover === false) {
                this.showErrorPopover = true;
            }
            this.formValid = false;
        }
    }

    // implicitly sets the this.formValid parameter.
    private isFormValid() {
        this.formValid = false;
        let invalidName = true;

        // if there are no errors, check to see if any data is dirty.
        if ((isUndefined(this.validateNameError)) || (this.validateNameError == null)) {
            if (!isUndefined(this.name) && (this.name.trim() !== '')) {
                const lowercaseName = this.name.trim().toLowerCase();
                if (this.originalName !== lowercaseName) {
                    this.formValid = true;
                }
                invalidName = false;
            }

            if ((invalidName === false) && (!isNullOrUndefined(this.description))) {
                if ((this.originalDescription !== this.description.trim())) {
                    this.formValid = true;
                }
            }
        }
        this.checkForDomainListReservedName();
        return;
    }

    isDirty(): boolean {
        let isDirty = false;
        if (this.isNameDirty() || this.isDescriptionDirty()) {
            isDirty = true;
        }
        return isDirty;
    }
}
